// Benefit of using useMemo/useCallback/etc as per https://kentcdodds.com/blog/usememo-and-usecallback

import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import useDeliverySlot from 'common/DeliverySlot/hooks/useDeliverySlot.hook';

import useExpress from 'pages/Checkout/components/Shipments/hooks/useExpress.hook';
import useApigeeLabels from 'common/ApigeeLabels/hooks/useApigeeLabels';

import { IShipmentsHook } from 'services/shipments/types/shipments.types';

import DynamicLoader from 'routes/DynamicLoader';
import useExpressGTM from 'pages/Checkout/components/Shipments/hooks/useExpressGTM.hook';
import NotifcationLayout from 'common/Notification/NotificationLayout';
import COLORS from 'theme/colors';
import { NowTabIcon } from '@mafc/icon';
import useNewC4Journey from 'hooks/useNewC4Journey';
import { NowJourneyIcon } from '@mafc/common';

const ShipmentLayout = DynamicLoader(() =>
  import(/* webpackChunkName: "ShipmentLayoutChunk" */ '../ShipmentLayout')
) as any;

interface Props {
  shipmentsHook: IShipmentsHook;
}

const ExpressShipment: React.FC<Props> = ({ shipmentsHook }) => {
  const {
    totalShipmentCount,
    express: expressEntries,
    hasExpress,
    expressDeliveryMessage,
    expressStoreUnavailable,
  } = shipmentsHook;
  const { t } = useTranslation();
  const { shipmentNow: shipmentLabel } = useApigeeLabels();
  // delivery slot state
  const [deliverySlot] = useDeliverySlot();
  const { loading, visible } = deliverySlot;
  const isNewC4Journey = useNewC4Journey();
  const [expressState] = useExpress(hasExpress);
  const { error: expressError, loading: expressLoading } = expressState;

  try {
    useExpressGTM(hasExpress, expressEntries.deliverySlot);
  } catch (error) {
    // tslint:disable-next-line: no-console
    console.error(error);
  }

  const deliveryMsg = useMemo(() => {
    if (expressDeliveryMessage) {
      if (expressStoreUnavailable) {
        return expressDeliveryMessage;
      } else {
        return `${t('shipmentLayout.deliveryIn')} ${expressDeliveryMessage}`;
      }
    } else {
      return t('shipmentLayout.delivery60min');
    }
  }, [expressDeliveryMessage, expressStoreUnavailable, t]);

  return (
    <>
      {expressError && <NotifcationLayout nonClosable>{expressError}</NotifcationLayout>}
      {!!(expressEntries && expressEntries.entries && expressEntries.entries.length) && (
        <ShipmentLayout
          shipmentTotal={expressEntries.shipmentTotal}
          entries={expressEntries.entries}
          deliveryFee={expressEntries.shipmentDeliveryFee}
          currentShipmentCount={expressEntries.currentShipmentCount}
          totalShipmentCount={totalShipmentCount}
          entriesCount={expressEntries.entries.length}
          deliveryHeading={deliveryMsg}
          deliveryHeadingIcon={
            isNewC4Journey ? (
              <NowTabIcon size="custom" width="24px" fill={COLORS.BLACK} />
            ) : (
              <NowJourneyIcon variant="new" iconProps={{ height: '20px', width: '60px' }} />
            )
          }
          loading={(loading && !visible) || expressLoading}
          isExpressShipment
          shipmentName={shipmentLabel}
        />
      )}
    </>
  );
};

export default memo(ExpressShipment);
